import React, { useState, useEffect } from "react";
import {
  Button,
  Input,
  Modal,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  useDisclosure,
  Select,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  SelectItem,
  Chip,
} from "@nextui-org/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { usersAPI, clientsAPI } from "../services/api";

import { EyeIcon, EditIcon, DeleteIcon } from "@/components/icons";
import DefaultLayout from "@/layouts/default";

type User = {
  id: string;
  email: string;
  role: string;
};

type Client = {
  id: string;
  name: string;
  subdomain: string;
};

const UserManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [userRole, setUserRole] = useState("user");
  const [users, setUsers] = useState<User[]>([]);
  const [_clients, _setClients] = useState<any[]>([]);
  const [availableRoles, _setAvailableRoles] = useState<string[]>([
    "admin",
    "user",
    "superadmin",
  ]);
  const [currentTenant, setCurrentTenant] = useState<Client | null>(null);
  const [loading, setLoading] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const [currentUserRole, _setCurrentUserRole] = useState<string>(
    localStorage.getItem("role") || "",
  );
  const [currentUserClientId, _setCurrentUserClientId] = useState<
    string | null
  >(localStorage.getItem("clientId") || null);
  const isSuperAdmin = currentUserRole === "superadmin";

  // Get current tenant from localStorage if superadmin
  const getCurrentTenant = () => {
    if (isSuperAdmin) {
      return localStorage.getItem("tenant") || null;
    } else if (currentUserRole === "admin" && currentUserClientId) {
      // For tenant admins, we use their client ID directly
      return null;
    }

    return null;
  };

  // Fetch current tenant information if superadmin
  const fetchCurrentTenant = async () => {
    // Only superadmins use the tenant selector
    if (!isSuperAdmin) {
      // For tenant admins, get their client info directly
      if (currentUserRole === "admin" && currentUserClientId) {
        try {
          const response = await clientsAPI.getById(currentUserClientId);

          setCurrentTenant(response);
        } catch (error) {
          console.error("Error fetching client info:", error);
        }
      }

      return;
    }

    const tenantId = getCurrentTenant();

    if (!tenantId) {
      return;
    }

    try {
      const response = await clientsAPI.getById(tenantId);

      setCurrentTenant(response);
    } catch (error) {
      console.error("Error fetching current tenant:", error);
    }
  };

  const fetchUsers = async () => {
    // For superadmins, check if tenant is selected
    if (isSuperAdmin && !getCurrentTenant()) {
      setError(t("No tenant selected. Please select a tenant first."));

      return;
    }

    setLoading(true);
    try {
      const usersList = await usersAPI.getAll();

      setUsers(usersList);
    } catch (error) {
      console.error("Error fetching users:", error);
      setError(t("Failed to fetch users. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrentTenant();
    fetchUsers();
  }, []);

  useEffect(() => {
    // Reset error when email changes
    if (emailExists) {
      setEmailExists(false);
    }
  }, [email]);

  const handleView = (userId: string) => {
    navigate(`/user/${userId}`);
  };

  const handleEdit = (userId: string) => {
    navigate(`/edit-user/${userId}`);
  };

  const handleDelete = async (userId: string) => {
    if (confirm(t("Are you sure you want to delete this user?"))) {
      try {
        await usersAPI.delete(userId);
        fetchUsers();
      } catch (error) {
        console.error("Error deleting user:", error);
        setError(t("Failed to delete user. Please try again."));
      }
    }
  };

  const checkEmailExists = async () => {
    try {
      // Get the tenant ID if superadmin
      let clientId: string | number | undefined;

      if (isSuperAdmin) {
        const tenantId = getCurrentTenant();

        // Only use tenant ID if it's not null
        if (tenantId) {
          clientId = tenantId;
        }
      } else {
        // For tenant admins, use their client ID
        clientId = currentUserClientId || undefined;
      }

      // Call the API to check if email exists, passing the client ID
      const response = await usersAPI.checkEmail(email, clientId);

      if (response.exists) {
        setEmailExists(true);
        setError(
          t("This email is already registered. Please use a different email."),
        );

        return true;
      }

      return false;
    } catch (error) {
      console.error("Error checking email:", error);

      return false;
    }
  };

  const handleAddUser = async () => {
    setError(null);

    // For superadmins, check if tenant is selected
    if (isSuperAdmin && !getCurrentTenant()) {
      setError(t("No tenant selected. Please select a tenant first."));

      return;
    }

    if (password.length < 6) {
      setError(t("Password should be at least 6 characters long."));

      return;
    }

    // Check if email exists
    const exists = await checkEmailExists();

    if (exists) {
      return;
    }

    try {
      // Get the tenant ID if superadmin
      let clientId: string | number | undefined;

      if (isSuperAdmin) {
        const tenantId = getCurrentTenant();

        // Only use tenant ID if it's not null
        if (tenantId) {
          clientId = tenantId;
        }
      } else {
        // For tenant admins, use their client ID
        clientId = currentUserClientId || undefined;
      }

      await usersAPI.create({
        name: email.split("@")[0], // Use part of email as name
        email,
        password,
        password_confirmation: password, // Laravel typically requires confirmation
        role: userRole,
        client_id: clientId, // Use snake_case as that's what the Laravel API expects
      });

      setEmail("");
      setPassword("");
      setUserRole("user"); // Resetting role
      onClose();

      fetchUsers();
    } catch (error) {
      console.error("Error adding user:", error);
      setError(
        t(
          "Failed to add user. Please check the email and password and try again.",
        ),
      );
    }
  };

  const handleRoleChange = (value: string) => {
    setUserRole(value);
  };

  const columns = [
    { uid: "email", name: t("Email") },
    { uid: "role", name: t("Role") },
    { uid: "actions", name: t("Actions") },
  ];

  // If the user is not a superadmin or tenant admin, show access denied
  if (currentUserRole !== "superadmin" && currentUserRole !== "admin") {
    return (
      <DefaultLayout>
        <div className="p-4">
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
            <p className="font-bold">{t("Access Denied")}</p>
            <p>{t("You do not have permission to manage users.")}</p>
          </div>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">{t("User Management")}</h1>
          {currentTenant && (
            <Chip color="primary">
              {t("Client")}: {currentTenant.name}
            </Chip>
          )}
        </div>

        {/* Only show tenant selection warning for superadmins without a tenant */}
        {isSuperAdmin && !getCurrentTenant() && (
          <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4">
            <p className="font-bold">{t("No Tenant Selected")}</p>
            <p>
              {t(
                "Please select a tenant from the tenant selector in the navbar to manage users.",
              )}
            </p>
          </div>
        )}

        <div className="mb-4">
          <Button
            color="primary"
            isDisabled={isSuperAdmin && !getCurrentTenant()}
            onPress={onOpen}
          >
            {t("Add New User")}
          </Button>

          <Modal isOpen={isOpen} placement="top-center" onClose={onClose}>
            <ModalContent>
              {(onClose) => (
                <>
                  <ModalHeader className="flex flex-col gap-1">
                    {t("Add New User")}
                  </ModalHeader>
                  <ModalBody>
                    {error && <p className="text-red-500 mb-2">{error}</p>}
                    <Input
                      fullWidth
                      isClearable
                      color={emailExists ? "danger" : "default"}
                      errorMessage={
                        emailExists ? t("Email already in use") : ""
                      }
                      isInvalid={emailExists}
                      label={t("Email")}
                      placeholder={t("Enter user email")}
                      type="email"
                      value={email}
                      variant="bordered"
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <Input
                      fullWidth
                      isClearable
                      label={t("Password")}
                      placeholder={t("Enter user password")}
                      type="password"
                      value={password}
                      variant="bordered"
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <Select
                      label={t("Role")}
                      selectedKeys={[userRole]}
                      onSelectionChange={(keys) =>
                        handleRoleChange(Array.from(keys)[0] as string)
                      }
                    >
                      {availableRoles.map((role) => (
                        <SelectItem key={role} value={role}>
                          {role}
                        </SelectItem>
                      ))}
                    </Select>
                  </ModalBody>
                  <ModalFooter>
                    <Button color="danger" variant="flat" onPress={onClose}>
                      {t("Close")}
                    </Button>
                    <Button color="primary" onPress={handleAddUser}>
                      {t("Add User")}
                    </Button>
                  </ModalFooter>
                </>
              )}
            </ModalContent>
          </Modal>
        </div>

        <h2 className="text-xl font-semibold mb-4">{t("User List")}</h2>

        {loading ? (
          <p>{t("Loading...")}</p>
        ) : (
          <Table aria-label={t("User List")}>
            <TableHeader columns={columns}>
              {(column) => (
                <TableColumn
                  key={column.uid}
                  align={column.uid === "actions" ? "center" : "start"}
                >
                  {column.name}
                </TableColumn>
              )}
            </TableHeader>
            <TableBody emptyContent={t("No users found")} items={users}>
              {(user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell className="flex gap-2 justify-center">
                    <Tooltip content={t("View user")}>
                      <Button
                        isIconOnly
                        aria-label={t("View user")}
                        size="sm"
                        variant="light"
                        onClick={() => handleView(user.id)}
                      >
                        <EyeIcon />
                      </Button>
                    </Tooltip>
                    <Tooltip content={t("Edit user")}>
                      <Button
                        isIconOnly
                        aria-label={t("Edit user")}
                        size="sm"
                        variant="light"
                        onClick={() => handleEdit(user.id)}
                      >
                        <EditIcon />
                      </Button>
                    </Tooltip>
                    <Tooltip color="danger" content={t("Delete user")}>
                      <Button
                        isIconOnly
                        aria-label={t("Delete user")}
                        color="danger"
                        size="sm"
                        variant="light"
                        onClick={() => handleDelete(user.id)}
                      >
                        <DeleteIcon />
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </DefaultLayout>
  );
};

export default UserManagementPage;
